import React, { useEffect, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import { CalibrationHistoryEntry, Skill } from 'talent_matching/types';
import { SecondaryTag } from '@grnhse/seedling/lib/birch/components/tags';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import Container from '@grnhse/seedling/lib/birch/components/container';
import CalibrationSkillsPanel from './calibration_form/calibration_skills_panel';
import CalibrationDetailsPanel from './calibration_form/calibration_details_panel';

const t = withLocalePrefix('talent_matching.modals.calibration');

const HistoryPanel = styled.div`
  width: 100%;
  padding: 0;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`;

const HistoryFrame = styled.div`
  border-top: 1px solid ${FullPalette.GREY_600};
  margin: 0 -${Tokens.spacing['3']};
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 0.28fr auto 0.72fr;
  gap: 0;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
`;

const HistoryItems = styled(Stack)`
  padding: 0;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const HistoryMetaRow = styled(Stack)`
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Tokens.spacing['1']};
`;

const HistoryRowButton = styled.div<{ $selected?: boolean }>`
  width: 100%;
  background: ${({ $selected }) =>
    $selected ? FullPalette.GREY_E500 : 'transparent'};
  border: none;
  border-bottom: 1px solid ${FullPalette.GREY_E500};
  border-radius: 0;
  padding: ${Tokens.spacing['1.5']} ${Tokens.spacing['2']};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${FullPalette.GREY_E500};
    outline: none;
  }
`;

const HistoryRowContent = styled(Stack)`
  width: 100%;
  gap: ${Tokens.spacing['0.25']};
`;

const ColumnDivider = styled.div`
  width: 1px;
  background-color: ${FullPalette.GREY_600};
  align-self: stretch;
  justify-self: center;
  height: 100%;
`;

const DetailColumn = styled(Stack)`
  width: 100%;
  padding: ${Tokens.spacing['2']};
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const ErrorText = styled(SecondaryBody)`
  color: ${Tokens.colors.error};
`;

const ReadOnlyHeaderContainer = styled(Container)`
  background-color: ${FullPalette.GREY_E25};
  border: 1px solid ${FullPalette.GREY_E500};
  box-shadow: none;
`;

type Props = {
  historyItems: CalibrationHistoryEntry[];
  selectedHistoryId: number | null;
  onSelectHistory: (id: number) => void;
  renderSkills: (
    disabled?: boolean,
    skillsOverride?: Skill[]
  ) => React.ReactNode;
  jobTitles: string[];
  onAddWorkTitle: (titles: string[]) => void;
  onRemoveWorkTitle: (keyword: string) => void;
  popperRef: React.RefObject<HTMLInputElement>;
  onUseVersion: (entry: CalibrationHistoryEntry) => void;
  onEditVersion: () => void;
  isLoading?: boolean;
  errorMessage?: string;
};

const HistoryCalibrationTab = ({
  historyItems,
  selectedHistoryId,
  onSelectHistory,
  renderSkills,
  jobTitles,
  onAddWorkTitle,
  onRemoveWorkTitle,
  popperRef,
  onUseVersion,
  onEditVersion,
  isLoading = false,
  errorMessage,
}: Props) => {
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const selectedHistory =
    historyItems.find((entry) => entry.id === selectedHistoryId) ||
    historyItems[0];
  const visibleItems = historyItems.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(10);
  }, [historyItems.length]);

  useEffect(() => {
    if (!selectedHistory) {
      return;
    }
    const idx = historyItems.findIndex(
      (entry) => entry.id === selectedHistory.id
    );
    if (idx >= visibleCount) {
      const nextCount = Math.ceil((idx + 1) / 10) * 10;
      setVisibleCount(nextCount);
    }
  }, [selectedHistory, historyItems, visibleCount]);

  const renderReadOnlyHeader = (entry: CalibrationHistoryEntry) => {
    const isActiveCalibration = entry.is_active;
    const buttonLabel = isActiveCalibration
      ? t('phase2.history_tab.edit_this_version')
      : t('phase2.history_tab.use_this_version');
    const buttonAction = isActiveCalibration
      ? () => onEditVersion()
      : () => onUseVersion(entry);

    return (
      <ReadOnlyHeaderContainer>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Stack spacing={0.25}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SecondaryBody>
                {t('phase2.history_tab.created_by')}{' '}
                <Body medium as="span">
                  {entry.updated_by_name}
                </Body>
              </SecondaryBody>
              {entry.is_active && (
                <SecondaryTag text={t('phase2.history_tab.active')} />
              )}
            </Stack>
            <SecondaryBody metadata>{entry.formatted_created_at}</SecondaryBody>
          </Stack>
          <SecondaryButton onClick={buttonAction} size="md">
            {buttonLabel}
          </SecondaryButton>
        </Stack>
      </ReadOnlyHeaderContainer>
    );
  };

  const renderHistoryList = () => {
    if (isLoading) {
      return <SkeletonLoader rows={4} columnsPerRow={1} />;
    }

    if (errorMessage) {
      return <ErrorText role="alert">{errorMessage}</ErrorText>;
    }

    if (historyItems.length === 0) {
      return (
        <SecondaryBody metadata>{t('phase2.history_tab.empty')}</SecondaryBody>
      );
    }

    return (
      <HistoryItems spacing={0}>
        {visibleItems.map((entry) => {
          const isSelected = selectedHistoryId === entry.id;
          const isActive = entry.is_active;
          const title =
            entry.updated_by_name ||
            t('phase2.history_tab.untitled_calibration');

          return (
            <HistoryRowButton
              key={entry.id}
              onClick={() => onSelectHistory(entry.id)}
              $selected={isSelected}
            >
              <HistoryRowContent>
                <HistoryMetaRow direction="row" spacing={0.5}>
                  <Stack spacing={0.25}>
                    <Body medium>{title}</Body>
                    <SecondaryBody metadata>
                      {entry.formatted_created_at}
                    </SecondaryBody>
                  </Stack>
                  {isActive && (
                    <SecondaryTag text={t('phase2.history_tab.active')} />
                  )}
                </HistoryMetaRow>
              </HistoryRowContent>
            </HistoryRowButton>
          );
        })}
        {historyItems.length > visibleCount && (
          <Stack alignItems="center" sx={{ padding: Tokens.spacing['1'] }}>
            <TertiaryButton
              onClick={() =>
                setVisibleCount(
                  Math.min(historyItems.length, visibleCount + 10)
                )
              }
            >
              {t('phase2.history_tab.show_more')}
            </TertiaryButton>
          </Stack>
        )}
      </HistoryItems>
    );
  };

  const renderHistoryDetail = () => {
    if (errorMessage) {
      return <ErrorText role="alert">{errorMessage}</ErrorText>;
    }

    if (!selectedHistory) {
      if (isLoading) {
        return <SkeletonLoader rows={8} columnsPerRow={1} />;
      }

      return (
        <SecondaryBody metadata>
          {t('phase2.history_tab.right_placeholder')}
        </SecondaryBody>
      );
    }

    return (
      <Stack spacing={2}>
        {renderReadOnlyHeader(selectedHistory)}
        <CalibrationSkillsPanel
          disabled
          calibration={selectedHistory}
          renderSkills={renderSkills}
          suggestedSkillsError={undefined}
        />
        <CalibrationDetailsPanel
          disabled
          calibration={selectedHistory}
          jobTitles={jobTitles}
          onAddWorkTitle={onAddWorkTitle}
          onRemoveWorkTitle={onRemoveWorkTitle}
          popperRef={popperRef}
        />
      </Stack>
    );
  };

  return (
    <HistoryPanel>
      <HistoryFrame>
        <TwoColumnLayout>
          <Stack spacing={0} sx={{ width: '100%' }}>
            {renderHistoryList()}
          </Stack>
          <ColumnDivider aria-hidden />
          <DetailColumn spacing={2}>{renderHistoryDetail()}</DetailColumn>
        </TwoColumnLayout>
      </HistoryFrame>
    </HistoryPanel>
  );
};

export default HistoryCalibrationTab;
